import { type StoreName, type SkuId, storesWhichSupportRecommendationsCalculation } from '@bluebrick/discounts-service-module'
import { type DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { now } from '@/common/dateUtils'
import {
	fetchDiscountConfig as fetchConfig,
	fetchDurationsForStoreSkus,
	fetchOpportunityNearestStartDate,
	fetchRecommendedDiscountDepth,
	fetchRecommendedDates as fetchDates
} from '@/features/eventDetails/eventDetailsRemoteRepository'
import { getDefaultNearestStartDate } from '@/features/eventDetails/eventDetailsUtils'
import { type RecommededDepthsMap, type EventDurationsMap } from '@/features/eventDetails/types'
import { type DiscountConfig, type EventData, type RecommendedDates } from '@/types'

const defaultDiscountConfig: DiscountConfig = {
	minDepth: 1,
	maxDepth: 99,
	minLengthInDays: 1,
	maxLengthInDays: 14,
	optimalLengthInDays: 14,
	startTimeRules: { hour: { default: 10 }, minute: { default: 0 }, second: { default: 0 } },
	endTimeRules: { hour: { default: 10 }, minute: { default: 0 }, second: { default: 0 } },
	ianaTimeZoneName: 'local',
	leadTimeInDays: 0
}

export const useEventDetailsRemoteDataStore = defineStore('eventDetailsRemoteData', () => {
	const hasDataError = ref(false)
	// Nearest date/hour that an event can start at. Points to 18:00 this or next day
	const discountConfig = ref<DiscountConfig>(defaultDiscountConfig)
	// A map which stores maximal and optimal event lengths for each currently selected SKU (only
	// for SKUs for which these lengths can be calculated in API)
	const nearestStartDate = ref<DateTime<true>>(getDefaultNearestStartDate(discountConfig.value))
	// Default discount config values (minimal and optimal length, cooldown lengths, etc.) for current store
	const eventDurations = ref<EventDurationsMap>(new Map())
	// A map which stores recommended discount depth for each currently selected SKU (only for SKUs
	// for which depths can be calculated in API)
	const recommendedDiscountDepth = ref<RecommededDepthsMap>(new Map())
	// A map which stores recommended event start and end dates for each currently selected SKU (only
	// for SKUs for which these dates can be calculated in API)
	const recommendedDates = ref(new Map<SkuId, RecommendedDates>())

	function canCalculateEventParameters(storeId: StoreName) {
		return storesWhichSupportRecommendationsCalculation.includes(storeId)
	}

	async function fetchNearestStartDate(storeId: StoreName) {
		if (hasDataError.value) {
			return
		}

		const date = await fetchOpportunityNearestStartDate(storeId)

		if (date && date.isValid) {
			nearestStartDate.value = date
		} else {
			nearestStartDate.value = getDefaultNearestStartDate(discountConfig.value)
		}
	}

	async function fetchDiscountConfig(storeId: StoreName, startDate: DateTime, endDate?: DateTime) {
		if (hasDataError.value || !startDate?.isValid) {
			return
		}

		// Only the start date actually matters here
		const config = await fetchConfig(storeId, startDate, endDate ?? startDate.plus({ day: 1 }))

		if (config) {
			discountConfig.value = config
		} else {
			hasDataError.value = true

			discountConfig.value = defaultDiscountConfig
		}

		return
	}

	async function fetchRecommendedDates(storeId: StoreName, startDate: DateTime, endDate: DateTime, discountEvents: EventData[]) {
		if (!canCalculateEventParameters(storeId)) {
			recommendedDates.value.clear()

			return
		}

		const dates = await fetchDates(storeId, startDate, endDate, (await fetchOpportunityNearestStartDate(storeId)) ?? now(), discountEvents)

		if (dates) {
			Object.entries(dates).forEach(([skuId, recommendation]) => {
				recommendedDates.value.set(skuId, recommendation)
			})
		} else {
			recommendedDates.value.clear()

			hasDataError.value = true
		}
	}

	async function setDefaultDurations(storeId: StoreName, startDate: DateTime, endDate: DateTime | undefined, selectedSkuIds: SkuId[]) {
		eventDurations.value.clear()

		await fetchDiscountConfig(storeId, startDate, endDate)

		selectedSkuIds.forEach((skuId) => {
			const maxLength = discountConfig.value?.maxLengthInDays
			const optimalLength = discountConfig.value?.optimalLengthInDays

			eventDurations.value.set(
				skuId,
				!!maxLength && !!optimalLength
					? {
							maxLength,
							optimalLength
						}
					: undefined
			)
		})
	}

	async function fetchEventMaxAndOptimalLengths(
		storeId: StoreName,
		startDate: DateTime,
		endDate: DateTime,
		selectedSkuIds: SkuId[],
		events: EventData[],
		cooldowns: EventData[]
	) {
		if (hasDataError.value || !startDate?.isValid || !selectedSkuIds.length) {
			// Nothing to do if required params are empty or not valid
			eventDurations.value.clear()

			return
		}

		if (!canCalculateEventParameters(storeId)) {
			// No recommendations for this store. Use values from discountConfig
			setDefaultDurations(storeId, startDate, endDate, selectedSkuIds)

			return
		}

		const durations = await fetchDurationsForStoreSkus(storeId, startDate.toUTC(), selectedSkuIds, events, cooldowns)

		if (durations) {
			Object.entries(durations).forEach(([key, val]) => eventDurations.value.set(key, val))
		} else {
			eventDurations.value.clear()

			hasDataError.value = true
		}
	}

	async function fetchRecommendedDepths(storeId: StoreName, startDate: DateTime, endDate: DateTime) {
		if (!canCalculateEventParameters(storeId) || !storeId || !startDate.isValid || !endDate.isValid) {
			return
		}

		const depths = await fetchRecommendedDiscountDepth(storeId, startDate.toUTC(), endDate.toUTC())

		if (depths) {
			Object.entries(depths).forEach(([key, val]) => recommendedDiscountDepth.value.set(key, val))
		} else {
			recommendedDiscountDepth.value.clear()

			hasDataError.value = true
		}
	}

	function reset() {
		hasDataError.value = false
		nearestStartDate.value = getDefaultNearestStartDate(discountConfig.value)
		discountConfig.value = defaultDiscountConfig
		eventDurations.value.clear()
		recommendedDiscountDepth.value.clear()
		recommendedDates.value.clear()
	}

	return {
		fetchDiscountConfig,
		fetchNearestStartDate,
		fetchRecommendedDates,
		fetchRecommendedDepths,
		fetchEventMaxAndOptimalLengths,
		reset,
		discountConfig: computed(() => discountConfig.value),
		nearestStartDate: computed(() => nearestStartDate.value),
		eventDurations: computed(() => eventDurations.value),
		recommendedDates: computed(() => recommendedDates.value),
		recommendedDiscountDepth: computed(() => recommendedDiscountDepth.value)
	}
})
